import React, { useRef, useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import {
    PAGE_BG, CARD_BG, BORDER, TEXT_MAIN, TEXT_MUTED,
    PRIMARY, CARD_RADIUS, FONT, WARNING_COLOR, ERROR_COLOR,
    SUCCESS_COLOR, SUCCESS_HOVER, PROGRESS_BG, INPUT_BG, INPUT_RADIUS,
    FS_SUBTITLE, FS_BODY, FS_LABEL,
} from './tokens';

const FIVE_MINUTES = 300;
const ONE_MINUTE = 60;

function formatTime(seconds) {
    if (seconds < 0) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = String(seconds % 60).padStart(2, '0');
    return `${m}:${s}`;
}

export default function QuizQuestion({
    questions,
    questionIndex,
    questionCount,
    answers,
    minLengths,
    secondsLeft,
    onAnswerChange,
    onNavigate,
    onFocusQuestion,
    onSubmit,
    t,
}) {
    const textareaRefs = useRef([]);
    const cardRefs = useRef([]);
    const scrollRef = useRef(null);

    // Al hacer scroll, la pregunta cuyo centro queda más cerca del centro del
    // área visible pasa a ser la "actual" (resaltado + opacidad), sin robar el
    // foco del navegador.
    useEffect(() => {
        const root = scrollRef.current;
        if (!root) return;
        let raf = null;
        const update = () => {
            raf = null;
            const rootRect = root.getBoundingClientRect();
            const center = rootRect.top + rootRect.height / 2;
            let best = -1;
            let bestDist = Infinity;
            cardRefs.current.forEach((el, i) => {
                if (!el) return;
                const r = el.getBoundingClientRect();
                const dist = Math.abs((r.top + r.height / 2) - center);
                if (dist < bestDist) { bestDist = dist; best = i; }
            });
            if (best !== -1) onFocusQuestion(best);
        };
        const onScroll = () => { if (raf == null) raf = requestAnimationFrame(update); };
        root.addEventListener('scroll', onScroll, { passive: true });
        update();
        return () => {
            root.removeEventListener('scroll', onScroll);
            if (raf != null) cancelAnimationFrame(raf);
        };
    }, [questionCount, onFocusQuestion]);

    const completedCount = answers.filter((a, i) => (a?.answer?.length || 0) >= (minLengths[i] || 0)).length;
    const allComplete = completedCount === questionCount;
    const progress = Math.round((completedCount / questionCount) * 100);

    const isUrgent = secondsLeft < ONE_MINUTE;
    const isWarning = secondsLeft < FIVE_MINUTES;
    const timerColor = isWarning ? (isUrgent ? ERROR_COLOR : WARNING_COLOR) : PRIMARY;

    function focusQuestion(i) {
        onNavigate(i);
        const el = textareaRefs.current[i];
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.focus({ preventScroll: true });
        }
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: PAGE_BG, fontFamily: FONT }}>
            <Box sx={{ px: { xs: 2, sm: 4 }, py: { xs: 2, sm: 2.75 }, backgroundColor: CARD_BG, borderBottom: `1px solid ${BORDER}`, position: 'sticky', top: 0, zIndex: 10 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2.5 } }}>
                        <Typography sx={{ color: TEXT_MUTED, fontWeight: 600, fontSize: { xs: FS_BODY, sm: '1.05rem' }, letterSpacing: '0.01em', whiteSpace: 'nowrap' }}>
                            {t('question')} {questionIndex + 1} / {questionCount}
                        </Typography>
                    </Box>
                    <Box sx={{ width: '50%', display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ flex: 1, display: 'flex', gap: 0.75 }}>
                            {minLengths.map((minLen, i) => {
                                const isComplete = (answers[i]?.answer?.length || 0) >= minLen;
                                const isCurrent = i === questionIndex;
                                return (
                                    <Box
                                        key={i}
                                        onClick={() => focusQuestion(i)}
                                        title={`${t('question')} ${i + 1}`}
                                        sx={{
                                            flex: 1,
                                            height: 8,
                                            borderRadius: 4,
                                            backgroundColor: isComplete ? SUCCESS_COLOR : isCurrent ? PRIMARY : PROGRESS_BG,
                                            cursor: 'pointer',
                                            outline: isCurrent ? `2px solid ${PRIMARY}` : 'none',
                                            outlineOffset: 2,
                                            transition: 'background-color 0.25s',
                                        }}
                                    />
                                );
                            })}
                        </Box>
                        <Typography sx={{ fontSize: { xs: FS_LABEL, sm: FS_BODY }, color: TEXT_MUTED, whiteSpace: 'nowrap', fontWeight: 600 }}>
                            {progress}% {t('quiz_complete')}
                        </Typography>
                    </Box>
                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
                        {allComplete && (
                            <Button
                                variant="contained"
                                onClick={onSubmit}
                                sx={{
                                    backgroundColor: SUCCESS_COLOR,
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: FS_BODY,
                                    textTransform: 'none',
                                    borderRadius: INPUT_RADIUS,
                                    px: 2.5,
                                    py: 0.75,
                                    whiteSpace: 'nowrap',
                                    boxShadow: '0 0 0 3px rgba(22,163,74,0.25)',
                                    animation: 'pulse-green 1.8s ease-in-out infinite',
                                    '@keyframes pulse-green': {
                                        '0%, 100%': { boxShadow: '0 0 0 3px rgba(22,163,74,0.25)' },
                                        '50%': { boxShadow: '0 0 0 6px rgba(22,163,74,0)' },
                                    },
                                    '&:hover': { backgroundColor: SUCCESS_HOVER },
                                }}
                            >
                                {t('send')}
                            </Button>
                        )}
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.75,
                                color: timerColor,
                                transition: 'color 0.3s',
                                animation: isUrgent ? 'pulse-timer 1s ease-in-out infinite' : 'none',
                                '@keyframes pulse-timer': {
                                    '0%, 100%': { opacity: 1 },
                                    '50%': { opacity: 0.55 },
                                },
                            }}
                        >
                            <AccessTimeRoundedIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                            <Box
                                component="span"
                                sx={{
                                    fontWeight: 800,
                                    fontSize: { xs: '1.25rem', sm: '1.6rem' },
                                    lineHeight: 1,
                                    fontVariantNumeric: 'tabular-nums',
                                    letterSpacing: '0.04em',
                                }}
                            >
                                {formatTime(secondsLeft)}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', p: { xs: 2, sm: 3 } }}>
                <Box sx={{ maxWidth: 1040, mx: 'auto', display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
                    {questions.map((question, i) => {
                        const answer = answers[i]?.answer || '';
                        const isCurrent = i === questionIndex;
                        const belowMin = answer.length < question.min_length;
                        return (
                            <Box
                                key={question.id ?? i}
                                ref={el => { cardRefs.current[i] = el; }}
                                sx={{
                                    backgroundColor: CARD_BG,
                                    borderRadius: CARD_RADIUS,
                                    border: `${isCurrent ? 2 : 1}px solid ${isCurrent ? PRIMARY : BORDER}`,
                                    boxShadow: isCurrent ? '0 0 0 3px rgba(31,58,138,0.10)' : 'none',
                                    opacity: isCurrent ? 1 : 0.6,
                                    p: { xs: 2.5, sm: 4 },
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 3,
                                    transition: 'border-color 0.2s, box-shadow 0.2s, opacity 0.2s',
                                }}
                            >
                                <Typography
                                    component="h2"
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: FS_SUBTITLE,
                                        color: TEXT_MAIN,
                                        lineHeight: 1.55,
                                    }}
                                >
                                    {question.order}. {question.question_text}
                                </Typography>

                                <Box>
                                    <textarea
                                        ref={el => { textareaRefs.current[i] = el; }}
                                        value={answer}
                                        onChange={e => onAnswerChange(e, i)}
                                        onFocus={e => { e.target.style.borderColor = PRIMARY; onFocusQuestion(i); }}
                                        onBlur={e => { e.target.style.borderColor = BORDER; }}
                                        onPaste={e => e.preventDefault()}
                                        rows={12}
                                        style={{
                                            width: '100%',
                                            boxSizing: 'border-box',
                                            padding: '12px 14px',
                                            fontSize: '0.9375rem', // FS_BODY (textarea nativo, no admite responsive object)
                                            fontFamily: FONT,
                                            color: TEXT_MAIN,
                                            backgroundColor: INPUT_BG,
                                            border: `1.5px solid ${isCurrent ? PRIMARY : BORDER}`,
                                            borderRadius: INPUT_RADIUS,
                                            resize: 'vertical',
                                            lineHeight: 1.65,
                                            outline: 'none',
                                            transition: 'border-color 0.15s',
                                        }}
                                    />
                                    {belowMin && (
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                                            <Typography
                                                sx={{
                                                    fontSize: FS_LABEL,
                                                    color: TEXT_MUTED,
                                                    fontVariantNumeric: 'tabular-nums',
                                                }}
                                            >
                                                {t('characters')}: {answer.length} / {t('min')} {question.min_length}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
}
